import React, { useState } from "react";
import { Box } from "@mui/material";
import { StriimTypography, StriimIconButton } from "@striim/striim-ui";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import useCopyToClipboard from "src/hooks/use-copy-to-clipboard";
import growl from "app/components/common/growl";
import styles from "./mcp-page-header.styles";

interface McpPageHeaderProps {
    endpoint: string;
}

const McpPageHeader: React.FC<McpPageHeaderProps> = ({ endpoint }) => {
    const { copy } = useCopyToClipboard();
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await copy(endpoint);
            setCopied(true);
            growl.success("Copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            growl.error("Failed to copy", "Copy to clipboard error");
        }
    };

    return (
        <Box sx={styles.root}>
            <StriimTypography variant="h1" sx={styles.title}>
                Striim Model Context Protocol (MCP)
            </StriimTypography>
            <StriimTypography variant="body4" sx={styles.subtitle}>
                Striim embeds an MCP server that lets AI clients drive Striim via natural language. Build pipelines, run
                queries, manage users, monitor apps, and more without leaving your Al assistant.
            </StriimTypography>
            <Box sx={styles.endpointRow}>
                <StriimTypography variant="overline" sx={styles.endpointLabel}>
                    Server endpoint
                </StriimTypography>
                <Box sx={styles.endpointPill}>
                    <Box component="span" sx={styles.endpointValue} data-testid="endpoint-value">
                        {endpoint}
                    </Box>
                    <StriimIconButton
                        size="small"
                        onClick={handleCopy}
                        sx={copied ? styles.copyButtonCopied : styles.copyButton}
                        data-testid="copy-endpoint-button"
                        title={copied ? "Copied!" : "Copy endpoint"}
                    >
                        {copied ? <CheckRoundedIcon fontSize="small" /> : <ContentCopyRoundedIcon fontSize="small" />}
                    </StriimIconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default McpPageHeader;
